/** @format */
import { ChangeDetectionStrategy, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { UrlService } from '@uirouter/core';

interface ShibbolethErrorParams {
    now?: string;
    errorType?: string;
    entityID?: string;
    statusCode2?: string;
    RelayState?: string;
}

type Bucket = 'SUOMIFI_AUTHN_FAILED' | 'INSTITUTIONAL_AUTHN_FAILED' | 'UNKNOWN_PRINCIPAL' | 'TECHNICAL' | 'UNKNOWN';

interface Diagnostics {
    now?: string;
    entityID?: string;
    statusFull?: string;
    errorType?: string;
}

const SUOMIFI_IDP_HOSTS = new Set(['tunnistautuminen.suomi.fi', 'testi.apro.tunnistus.fi']);

const KNOWN_APPS = ['student', 'staff', 'teacher', 'tutor', 'admin'] as const;
type KnownApp = (typeof KNOWN_APPS)[number];
const DEFAULT_APP: KnownApp = 'student';

@Component({
    selector: 'app-login-error',
    templateUrl: './login-error.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class LoginErrorComponent implements OnInit {
    titleKey!: string;
    descriptionKey!: string;
    primaryActionTarget!: string;
    diagnostics: Diagnostics = {};
    hasDiagnostics = false;

    constructor(private urlService: UrlService) {}

    ngOnInit(): void {
        const params = (this.urlService.search() ?? {}) as ShibbolethErrorParams;
        const bucket = this.classify(params);

        this.titleKey = `LOGIN_ERROR.${bucket}.TITLE`;
        this.descriptionKey = `LOGIN_ERROR.${bucket}.DESCRIPTION`;
        this.primaryActionTarget = `/${this.deriveAppFromRelayState(params.RelayState)}/login`;
        this.diagnostics = this.buildDiagnostics(params);
        this.hasDiagnostics = Object.values(this.diagnostics).some(v => !!v);
    }

    onPrimaryAction(): void {
        window.location.assign(this.primaryActionTarget);
    }

    private classify(params: ShibbolethErrorParams): Bucket {
        const subStatus = lastUrnSegment(params.statusCode2);
        const errorType = params.errorType ?? '';
        const entityID = params.entityID ?? '';

        switch (subStatus) {
            case 'AuthnFailed':
                return this.isSuomiFiEntityId(entityID) ? 'SUOMIFI_AUTHN_FAILED' : 'INSTITUTIONAL_AUTHN_FAILED';
            case 'UnknownPrincipal':
                return 'UNKNOWN_PRINCIPAL';
            default:
                if (errorType.startsWith('opensaml::')) return 'TECHNICAL';
                return 'UNKNOWN';
        }
    }

    private isSuomiFiEntityId(entityID: string): boolean {
        try {
            return SUOMIFI_IDP_HOSTS.has(new URL(entityID).hostname.toLowerCase());
        } catch {
            return false;
        }
    }

    private buildDiagnostics(params: ShibbolethErrorParams): Diagnostics {
        return {
            now: params.now,
            entityID: params.entityID,
            statusFull: params.statusCode2,
            errorType: params.errorType,
        };
    }

    private deriveAppFromRelayState(maybeUrl: string | undefined): KnownApp {
        if (!maybeUrl) return DEFAULT_APP;
        try {
            const path = new URL(maybeUrl, window.location.origin).pathname;
            const firstSegment = path.split('/').find(segment => segment.length > 0);
            return firstSegment && (KNOWN_APPS as readonly string[]).includes(firstSegment) ? (firstSegment as KnownApp) : DEFAULT_APP;
        } catch {
            return DEFAULT_APP;
        }
    }
}

function lastUrnSegment(urn: string | undefined): string | undefined {
    if (!urn) return undefined;
    const idx = urn.lastIndexOf(':');
    return idx === -1 ? urn : urn.substring(idx + 1);
}
